/**
 * Global imperative Confirm / Prompt dialogs.
 *
 * Replaces native `window.confirm` / `window.prompt` (which on Android TWA
 * render the awful "The page at https://… says" header) with styled Radix
 * AlertDialog / Dialog components.
 *
 * Usage anywhere — no React context required at the call site:
 *
 *   import { confirmDialog, promptDialog } from '@/hooks/useConfirm';
 *
 *   if (await confirmDialog({ title: 'Delete?', description: '…', variant: 'danger' })) { … }
 *   const reason = await promptDialog({ title: 'Reason', label: 'Why?', defaultValue: '' });
 *
 * Both return a Promise that resolves AFTER the user clicks an action.
 * `confirmDialog` resolves to `boolean`; `promptDialog` resolves to `string | null`
 * (null when cancelled).
 *
 * Mount `<ConfirmProvider />` ONCE near the React root.
 */
import { useEffect, useState, useRef } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MessageSquareText } from 'lucide-react';

let confirmHandler = null;
let promptHandler = null;

/**
 * @param {object} opts
 * @param {string} [opts.title]
 * @param {string} [opts.description]
 * @param {string} [opts.confirmText]  default 'Confirm'
 * @param {string} [opts.cancelText]   default 'Cancel'
 * @param {'default'|'danger'} [opts.variant]
 * @returns {Promise<boolean>}
 */
export const confirmDialog = (opts = {}) => {
  if (!confirmHandler) {
    return Promise.resolve(window.confirm(opts.description || opts.title || 'Confirm?'));
  }
  return confirmHandler(opts);
};

/**
 * @param {object} opts
 * @param {string} [opts.title]
 * @param {string} [opts.label]
 * @param {string} [opts.placeholder]
 * @param {string} [opts.defaultValue]
 * @param {string} [opts.type]   'text' | 'number' | 'date'
 * @param {boolean} [opts.required]
 * @returns {Promise<string|null>}
 */
export const promptDialog = (opts = {}) => {
  if (!promptHandler) {
    const v = window.prompt(opts.label || opts.title || 'Enter:', opts.defaultValue || '');
    return Promise.resolve(v);
  }
  return promptHandler(opts);
};

export function ConfirmProvider({ children }) {
  const [confirmState, setConfirmState] = useState(null);
  const [promptState, setPromptState] = useState(null);
  const promptValueRef = useRef('');

  useEffect(() => {
    confirmHandler = (opts) => new Promise((resolve) => setConfirmState({ ...opts, resolve }));
    promptHandler = (opts) => new Promise((resolve) => {
      promptValueRef.current = opts.defaultValue ?? '';
      setPromptState({ ...opts, resolve });
    });
    return () => { confirmHandler = null; promptHandler = null; };
  }, []);

  const closeConfirm = (val) => {
    if (confirmState) confirmState.resolve(val);
    setConfirmState(null);
  };

  const closePrompt = (val) => {
    if (promptState) promptState.resolve(val);
    setPromptState(null);
    promptValueRef.current = '';
  };

  return (
    <>
      {children}

      {/* Confirm dialog */}
      <AlertDialog open={!!confirmState} onOpenChange={(o) => !o && closeConfirm(false)}>
        <AlertDialogContent data-testid="global-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title || 'Are you sure?'}</AlertDialogTitle>
            {confirmState?.description && (
              <AlertDialogDescription className="whitespace-pre-wrap">
                {confirmState.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => closeConfirm(false)} data-testid="confirm-cancel">
              {confirmState?.cancelText || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => closeConfirm(true)}
              data-testid="confirm-ok"
              className={confirmState?.variant === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            >
              {confirmState?.confirmText || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Prompt dialog */}
      <Dialog open={!!promptState} onOpenChange={(o) => !o && closePrompt(null)}>
        <DialogContent className="sm:max-w-md" data-testid="global-prompt-dialog" aria-describedby={undefined}>
          <DialogHeader className="text-center sm:text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-forest-100 text-forest-700 flex items-center justify-center mb-1">
              {promptState?.icon || <MessageSquareText className="w-6 h-6" />}
            </div>
            <DialogTitle className="text-xl">{promptState?.title || 'Enter a value'}</DialogTitle>
            {promptState?.description && (
              <DialogDescription className="whitespace-pre-wrap">{promptState.description}</DialogDescription>
            )}
          </DialogHeader>
          {promptState && (
            <PromptBody
              state={promptState}
              valueRef={promptValueRef}
              onSubmit={(v) => closePrompt(v)}
              onCancel={() => closePrompt(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function PromptBody({ state, valueRef, onSubmit, onCancel }) {
  const [val, setVal] = useState(state.defaultValue ?? '');
  useEffect(() => { valueRef.current = val; }, [val, valueRef]);

  const submit = () => {
    if (state.required && !String(val).trim()) return;
    onSubmit(String(val));
  };

  const invalid = !!(state.required && !String(val).trim()) || !!(state.validate && state.validate(val));
  return (
    <form className="space-y-4 py-1" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <div className="rounded-lg bg-muted/60 p-4 space-y-2">
        {state.label && <Label className="text-sm" htmlFor="global-prompt-input">{state.label}{state.required ? '' : <span className="text-muted-foreground font-normal"> (optional)</span>}</Label>}
        {state.multiline ? (
          <Textarea id="global-prompt-input" autoFocus rows={3} value={val} placeholder={state.placeholder || ''} onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }} className="bg-background" data-testid="prompt-input" />
        ) : (
          <Input id="global-prompt-input" autoFocus type={state.type || 'text'} value={val} placeholder={state.placeholder || ''} onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }} className={`h-12 bg-background ${state.type === 'number' ? 'text-lg font-semibold' : ''}`}
            min={state.min} max={state.max} step={state.step} data-testid="prompt-input" />
        )}
        {state.hint && <p className="text-xs text-muted-foreground">{state.hint}</p>}
        {state.validate && state.validate(val) && String(val).length > 0 && <p className="text-xs text-red-600" data-testid="prompt-error">{state.validate(val)}</p>}
      </div>
      <div className="space-y-2">
        <Button type="submit" disabled={invalid} className={`w-full h-11 text-base ${state.variant === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-forest-700 hover:bg-forest-800 text-white'}`} data-testid="prompt-submit">
          {state.confirmText || 'OK'}
        </Button>
        <Button type="button" variant="outline" className="w-full h-11" onClick={onCancel} data-testid="prompt-cancel">
          {state.cancelText || 'Cancel'}
        </Button>
      </div>
    </form>
  );
}
